import type { QuotaValue } from "@enduring-systems/entitlements";
import { isUnlimited, UNLIMITED } from "@enduring-systems/entitlements";
import { useEffect, useRef, useState } from "react";

import { Checkbox } from "@/common/ui/checkbox";
import { Input } from "@/common/ui/input";
import { Label } from "@/common/ui/label";

export interface QuotaControlProps {
  readonly capabilityId: string;
  readonly value: QuotaValue;
  readonly onChange: (value: QuotaValue) => void;
  readonly disabled?: boolean;
}

export function QuotaControl({
  capabilityId,
  value,
  onChange,
  disabled = false,
}: QuotaControlProps): JSX.Element {
  const unlimited = isUnlimited(value);
  const lastFinite = useRef<number>(unlimited ? 0 : value);

  // Draft holds raw text only while empty or invalid, so clearing to retype never
  // emits a real 0; it resets whenever the resolved value changes.
  const [draft, setDraft] = useState<string | null>(null);
  useEffect(() => {
    setDraft(null);
    if (!isUnlimited(value)) {
      lastFinite.current = value;
    }
  }, [value]);

  const inputId = `${capabilityId}-quota`;
  const unlimitedId = `${capabilityId}-quota-unlimited`;

  function handleUnlimitedChange(checked: boolean): void {
    onChange(checked ? UNLIMITED : lastFinite.current);
  }

  function handleInputChange(raw: string): void {
    setDraft(raw);
    if (raw === "") {
      return;
    }
    const parsed = Number(raw);
    if (!Number.isInteger(parsed) || parsed < 0) {
      return;
    }
    onChange(parsed);
  }

  return (
    <div className="flex flex-col gap-2">
      <Input
        id={inputId}
        data-testid="entitlement-control-quota-input"
        type="number"
        min={0}
        step={1}
        value={unlimited ? "" : (draft ?? value)}
        disabled={disabled || unlimited}
        onChange={(event) => handleInputChange(event.target.value)}
      />
      <div className="flex items-center gap-2">
        <Checkbox
          id={unlimitedId}
          data-testid="entitlement-control-quota-unlimited"
          checked={unlimited}
          disabled={disabled}
          onCheckedChange={(checked) => handleUnlimitedChange(checked === true)}
        />
        <Label htmlFor={unlimitedId}>Unlimited</Label>
      </div>
    </div>
  );
}
